import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/Button';
import { StatusBadge } from '@/components/StatusBadge';
import { useToast } from '@/components/ToastProvider';
import { UpdateTaskStatusForm } from './UpdateTaskStatusForm';
import { useUpdateTask, useCancelTask } from '../hooks';
import type { JobTask, UpdateJobTaskInput } from '@maker-toolchest/shared';

interface JobTasksModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  jobName: string;
  tasks: JobTask[];
}

export function JobTasksModal({
  open,
  onClose,
  jobId,
  jobName,
  tasks,
}: JobTasksModalProps) {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const toast = useToast();

  const selectedTask = selectedTaskId ? tasks.find(t => t.id === selectedTaskId) : null;
  const updateTask = useUpdateTask(jobId, selectedTaskId || '');
  const cancelTask = useCancelTask(jobId, selectedTaskId || '');

  if (!open) return null;

  const handleUpdateTask = async (data: UpdateJobTaskInput) => {
    if (!selectedTaskId) return;

    try {
      setUpdateError(null);
      await updateTask.mutateAsync(data);
      setIsUpdateFormOpen(false);
      setSelectedTaskId(null);
      toast.success('Task updated successfully');
    } catch (error: any) {
      setUpdateError(error.message || 'Failed to update task');
    }
  };

  const handleCancelTask = async (taskId: string) => {
    if (!confirm('Are you sure you want to cancel this task?')) {
      return;
    }

    setSelectedTaskId(taskId);
    try {
      await cancelTask.mutateAsync(undefined);
      toast.success('Task cancelled');
    } catch (error: any) {
      toast.error(error.message || 'Failed to cancel task');
    } finally {
      setSelectedTaskId(null);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-4xl max-h-[80vh] bg-background rounded-lg border border-border shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Tasks for {jobName}</h2>
            <p className="text-sm text-foreground-muted mt-1">
              {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-foreground-muted hover:bg-background-hover hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-120px)] p-6">
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-foreground-muted">
              No tasks yet
            </div>
          ) : (
            <div className="overflow-x-auto rounded border border-border">
              <table className="w-full">
                <thead className="bg-background-subtle border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                      Task #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-foreground-muted uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-foreground-muted uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-background divide-y divide-border">
                  {tasks.map((task) => (
                    <tr key={task.id} className="hover:bg-background-subtle transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-foreground">
                          Task {task.taskNumber}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={task.status} size="sm" />
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-muted">
                          {Math.round(task.progress)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                  setSelectedTaskId(task.id);
                                  setIsUpdateFormOpen(true);
                                }}
                                disabled={updateTask.isPending || cancelTask.isPending}
                              >
                                Update
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancelTask(task.id)}
                                disabled={updateTask.isPending || cancelTask.isPending}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Update Task Status Form */}
      {selectedTask && (
        <UpdateTaskStatusForm
          open={isUpdateFormOpen}
          onClose={() => {
            setIsUpdateFormOpen(false);
            setSelectedTaskId(null);
            setUpdateError(null);
          }}
          onSubmit={handleUpdateTask}
          currentStatus={selectedTask.status}
          currentProgress={selectedTask.progress}
          loading={updateTask.isPending}
          error={updateError}
        />
      )}
    </>
  );
}
